import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useGameAuth } from "@/hooks/useGameAuth";
import { useGameAdmin } from "@/hooks/useGameAdmin";
import { GameLayout } from "@/components/game/GameLayout";
import { GameAuthForm } from "@/components/game/GameAuthForm";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Shield, AlertTriangle, Link2, Smartphone, Zap, Gamepad2, Check } from "lucide-react";
import { toast } from "sonner";

interface FraudLog {
  id: string;
  event_type: string;
  severity: string;
  player_id: string | null;
  target_player_id: string | null;
  nft_id: string | null;
  device_hash: string | null;
  ip_address: string | null;
  details: any;
  created_at: string;
}

interface AccountLink {
  id: string;
  player_a_id: string;
  player_b_id: string;
  link_type: string;
  confidence: number;
  whitelisted: boolean;
  created_at: string;
}

interface DeviceLink {
  id: string;
  device_hash: string;
  player_id: string;
  user_id: string;
  ip_address: string | null;
  user_agent: string | null;
  last_seen: string;
}

interface SIUnlock {
  id: string;
  player_id: string;
  sol_paid: number;
  sol_price_usd: number;
  tx_signature: string;
  unlocked_at: string;
}

interface PlayerNameMap { [id: string]: string; }

export default function GameAdmin() {
  const { session, player, loading: authLoading, signIn, signUp, signOut, resetPassword } = useGameAuth();
  const { isAdmin, loading: adminLoading } = useGameAdmin();

  const [fraudLogs, setFraudLogs] = useState<FraudLog[]>([]);
  const [links, setLinks] = useState<AccountLink[]>([]);
  const [devices, setDevices] = useState<DeviceLink[]>([]);
  const [purchases, setPurchases] = useState<SIUnlock[]>([]);
  const [names, setNames] = useState<PlayerNameMap>({});
  const [loadingData, setLoadingData] = useState(true);

  const loadAll = async () => {
    setLoadingData(true);
    try {
      const [logsRes, linksRes, devsRes, purRes] = await Promise.all([
        supabase.from("game_fraud_log").select("*").order("created_at", { ascending: false }).limit(200),
        supabase.from("game_account_links").select("*").order("created_at", { ascending: false }).limit(200),
        supabase.from("game_device_links").select("*").order("last_seen", { ascending: false }).limit(200),
        supabase.from("game_space_invaders_unlocks").select("*").order("unlocked_at", { ascending: false }).limit(200),
      ]);

      setFraudLogs((logsRes.data as any) || []);
      setLinks((linksRes.data as any) || []);
      setDevices((devsRes.data as any) || []);
      setPurchases((purRes.data as any) || []);

      // Resolve player names
      const ids = new Set<string>();
      ((logsRes.data as any) || []).forEach((l: FraudLog) => {
        if (l.player_id) ids.add(l.player_id);
        if (l.target_player_id) ids.add(l.target_player_id);
      });
      ((linksRes.data as any) || []).forEach((l: AccountLink) => {
        ids.add(l.player_a_id); ids.add(l.player_b_id);
      });
      ((devsRes.data as any) || []).forEach((d: DeviceLink) => ids.add(d.player_id));
      ((purRes.data as any) || []).forEach((p: SIUnlock) => ids.add(p.player_id));

      if (ids.size > 0) {
        const { data: players } = await supabase
          .from("game_players")
          .select("id, display_name")
          .in("id", Array.from(ids));
        const map: PlayerNameMap = {};
        (players || []).forEach((p: any) => { map[p.id] = p.display_name; });
        setNames(map);
      }
    } catch (e: any) {
      toast.error("Errore caricamento: " + e.message);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (isAdmin && session) loadAll();
  }, [isAdmin, session]);

  const whitelistLink = async (linkId: string) => {
    const { error } = await supabase
      .from("game_account_links")
      .update({ whitelisted: true })
      .eq("id", linkId);
    if (error) { toast.error(error.message); return; }
    toast.success("Link whitelisted");
    setLinks((prev) => prev.map((l) => l.id === linkId ? { ...l, whitelisted: true } : l));
  };

  if (authLoading || adminLoading) {
    return (
      <div className="h-dvh game-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="h-dvh game-bg flex items-center justify-center p-4">
        <GameAuthForm onSignIn={signIn} onSignUp={signUp} onResetPassword={resetPassword} />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/game/dashboard" replace />;
  }

  const sevColor = (s: string) =>
    s === "critical" ? "bg-red-500/20 text-red-300 border-red-500/40"
    : s === "warning" ? "bg-yellow-500/20 text-yellow-300 border-yellow-500/40"
    : "bg-blue-500/20 text-blue-300 border-blue-500/40";

  return (
    <GameLayout player={player} onSignOut={signOut}>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-7 h-7 text-purple-400" />
            <div>
              <h1 className="text-2xl font-extrabold text-game-gradient">Dashboard Admin</h1>
              <p className="text-xs text-gray-400">Antifrode Game — solo amministratore</p>
            </div>
          </div>
          <Button onClick={loadAll} disabled={loadingData} size="sm" variant="outline" className="border-purple-500/40 text-purple-300">
            {loadingData ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aggiorna"}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={AlertTriangle} label="Eventi frode" value={fraudLogs.length} color="text-red-400" />
          <StatCard icon={Link2} label="Account collegati" value={links.length} color="text-yellow-400" />
          <StatCard icon={Smartphone} label="Device tracciati" value={devices.length} color="text-cyan-400" />
          <StatCard icon={Gamepad2} label="Sblocchi Space Invaders" value={purchases.length} color="text-pink-400" />
        </div>

        <Tabs defaultValue="fraud" className="w-full">
          <TabsList className="bg-black/60 border border-purple-500/20">
            <TabsTrigger value="fraud" className="data-[state=active]:bg-purple-500/20">Eventi frode</TabsTrigger>
            <TabsTrigger value="links" className="data-[state=active]:bg-purple-500/20">Account collegati</TabsTrigger>
            <TabsTrigger value="devices" className="data-[state=active]:bg-purple-500/20">Device</TabsTrigger>
            <TabsTrigger value="purchases" className="data-[state=active]:bg-purple-500/20">Sblocchi</TabsTrigger>
          </TabsList>

          {/* FRAUD LOGS */}
          <TabsContent value="fraud">
            <Card className="bg-black/60 border-purple-500/20">
              <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" />Ultimi eventi antifrode</CardTitle></CardHeader>
              <CardContent className="space-y-2 max-h-[60vh] overflow-y-auto">
                {fraudLogs.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-6">Nessun evento registrato.</p>
                ) : fraudLogs.map((log) => (
                  <div key={log.id} className="rounded-lg border border-white/10 bg-white/5 p-3 text-xs">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={sevColor(log.severity)}>{log.severity}</Badge>
                        <span className="font-bold text-white">{log.event_type}</span>
                      </div>
                      <span className="text-gray-500">{new Date(log.created_at).toLocaleString("it-IT")}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-gray-400">
                      {log.player_id && <div>Giocatore: <span className="text-cyan-300">{names[log.player_id] || log.player_id.slice(0, 8)}</span></div>}
                      {log.target_player_id && <div>Target: <span className="text-pink-300">{names[log.target_player_id] || log.target_player_id.slice(0, 8)}</span></div>}
                      {log.ip_address && <div>IP: <span className="text-gray-300">{log.ip_address}</span></div>}
                      {log.device_hash && <div>Device: <span className="text-gray-300 font-mono">{log.device_hash.slice(0, 12)}…</span></div>}
                    </div>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <pre className="mt-1.5 text-[10px] text-gray-500 bg-black/40 rounded p-1.5 overflow-x-auto">{JSON.stringify(log.details, null, 2)}</pre>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ACCOUNT LINKS */}
          <TabsContent value="links">
            <Card className="bg-black/60 border-purple-500/20">
              <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Link2 className="w-4 h-4 text-yellow-400" />Account collegati (sospetti multi-account)</CardTitle></CardHeader>
              <CardContent className="space-y-2 max-h-[60vh] overflow-y-auto">
                {links.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-6">Nessun account collegato rilevato.</p>
                ) : links.map((l) => (
                  <div key={l.id} className="rounded-lg border border-white/10 bg-white/5 p-3 text-xs">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-cyan-300 font-bold">{names[l.player_a_id] || l.player_a_id.slice(0, 8)}</span>
                        <Link2 className="w-3 h-3 text-gray-500" />
                        <span className="text-pink-300 font-bold">{names[l.player_b_id] || l.player_b_id.slice(0, 8)}</span>
                      </div>
                      {l.whitelisted ? (
                        <Badge className="bg-green-500/20 text-green-300 border-green-500/40"><Check className="w-3 h-3 mr-1" />Whitelist</Badge>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => whitelistLink(l.id)} className="h-7 text-[11px] border-green-500/40 text-green-300 hover:bg-green-500/10">
                          Whitelist
                        </Button>
                      )}
                    </div>
                    <div className="text-gray-400">
                      Tipo: <span className="text-white">{l.link_type}</span> · Confidenza: <span className="text-yellow-300">{l.confidence}%</span> · {new Date(l.created_at).toLocaleString("it-IT")}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* DEVICES */}
          <TabsContent value="devices">
            <Card className="bg-black/60 border-purple-500/20">
              <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Smartphone className="w-4 h-4 text-cyan-400" />Device tracciati</CardTitle></CardHeader>
              <CardContent className="space-y-2 max-h-[60vh] overflow-y-auto">
                {devices.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-6">Nessun device tracciato.</p>
                ) : devices.map((d) => (
                  <div key={d.id} className="rounded-lg border border-white/10 bg-white/5 p-3 text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-cyan-300 font-bold">{names[d.player_id] || d.player_id.slice(0, 8)}</span>
                      <span className="text-gray-500">{new Date(d.last_seen).toLocaleString("it-IT")}</span>
                    </div>
                    <div className="text-gray-400 grid grid-cols-1 sm:grid-cols-2 gap-1">
                      <div>Device: <span className="text-gray-300 font-mono">{d.device_hash.slice(0, 16)}…</span></div>
                      {d.ip_address && <div>IP: <span className="text-gray-300">{d.ip_address}</span></div>}
                    </div>
                    {d.user_agent && <div className="text-[10px] text-gray-500 mt-1 truncate">{d.user_agent}</div>}
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SPACE INVADERS UNLOCKS */}
          <TabsContent value="purchases">
            <Card className="bg-black/60 border-purple-500/20">
              <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Gamepad2 className="w-4 h-4 text-pink-400" />Sblocchi Space Invaders</CardTitle></CardHeader>
              <CardContent className="space-y-2 max-h-[60vh] overflow-y-auto">
                {purchases.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-6">Nessuno sblocco registrato.</p>
                ) : purchases.map((p) => (
                  <div key={p.id} className="rounded-lg border border-white/10 bg-white/5 p-3 text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-cyan-300 font-bold">{names[p.player_id] || p.player_id.slice(0, 8)}</span>
                      <span className="text-gray-500">{new Date(p.unlocked_at).toLocaleString("it-IT")}</span>
                    </div>
                    <div className="text-gray-400 grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <div>SOL pagati: <span className="text-cyan-300">{Number(p.sol_paid).toFixed(6)}</span></div>
                      <div>SOL/USD: <span className="text-gray-300">${Number(p.sol_price_usd).toFixed(2)}</span></div>
                      <div>USD: <span className="text-emerald-400 font-bold">${(Number(p.sol_paid) * Number(p.sol_price_usd)).toFixed(2)}</span></div>
                    </div>
                    <div className="text-[10px] text-gray-500 mt-1 font-mono truncate">{p.tx_signature}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </GameLayout>
  );
}

function StatCard({ icon: Icon, label, value, color }: { icon: any; label: string; value: number; color: string }) {
  return (
    <Card className="bg-black/60 border-purple-500/20">
      <CardContent className="p-3 flex items-center gap-3">
        <Icon className={`w-6 h-6 ${color}`} />
        <div>
          <div className="text-xl font-extrabold text-white">{value}</div>
          <div className="text-[11px] text-gray-400">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}
